import { formatShortWeekdayDate, formatTime } from "@shared";
import { Locale, startOfDay } from "date-fns";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { AppointmentExchangeResponse } from "@/api";
import { formatCounselorLabel } from "@/common/utils/formatCounselorLabel";

import { ExchangeAppointmentSelectionValues, ExchangeRequestedServicesValues } from "./Exchange.types";

/**
 * Names the colleague who created a request the app-wide way — "MM (Max Mustermann)". Falls back to
 * the bare Kürzel carried on the row when the counselor is not in the (active-only) list.
 */
export function resolveExchangeCounselorLabel(
  exchange: Pick<AppointmentExchangeResponse, "createdByCounselorId" | "counselorUserCode">,
  counselors: Array<{ counselorId: string; userCode: string; firstName: string; lastName: string }>
): string {
  const requester = counselors.find((counselor) => counselor.counselorId === exchange.createdByCounselorId);
  return requester ? formatCounselorLabel(requester) : exchange.counselorUserCode;
}

/** "08:30 Uhr" — or "Ganztägig" for all-day Termine. */
export function formatExchangeAppointmentTime(
  startUtc: Date,
  isAllDay: boolean,
  locale: Locale | undefined,
  t: TFunction
): string {
  return isAllDay ? t("exchange.allDay") : `${formatTime(startUtc, locale)} ${t("common.clock")}`;
}

/** "Do, 23.07.2026, 08:30 Uhr" — or "Do, 23.07.2026, Ganztägig" for all-day Termine. */
export function formatExchangeAppointment(
  exchange: Pick<AppointmentExchangeResponse, "appointmentStartUtc" | "isAllDay">,
  locale: Locale | undefined,
  t: TFunction
): string {
  const date = formatShortWeekdayDate(exchange.appointmentStartUtc, locale);
  return `${date}, ${formatExchangeAppointmentTime(exchange.appointmentStartUtc, exchange.isAllDay, locale, t)}`;
}

/**
 * The Tauschbörse badge: offers on the user's own requests that they have not decided on yet.
 * Undefined while the list is still loading, so the badge stays hidden instead of flashing a zero.
 */
export function countExchangeBadge(
  exchanges: AppointmentExchangeResponse[] | undefined,
  ownCounselorId: string | null | undefined
): number | undefined {
  if (exchanges === undefined) {
    return undefined;
  }
  if (ownCounselorId == null) {
    return 0;
  }
  return exchanges
    .filter((exchange) => exchange.createdByCounselorId === ownCounselorId)
    .reduce((sum, exchange) => sum + exchange.pendingOffersCount, 0);
}

/**
 * The Yup rules shared by every dialog that picks one own Termin in a date range. The two label keys
 * differ per dialog because they name that dialog's own fields in the "is required" message.
 */
export function exchangeAppointmentSelectionRules(
  t: TFunction,
  fromLabel: string,
  tillLabel: string
): { [K in keyof ExchangeAppointmentSelectionValues]: Yup.Schema<ExchangeAppointmentSelectionValues[K]> } {
  return {
    from: Yup.date()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: fromLabel })) as Yup.Schema<Date | null>,
    till: Yup.date()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: tillLabel }))
      .test("till-not-before-from", t("exchange.validation.tillBeforeFrom"), function (till) {
        const { from } = this.parent as Pick<ExchangeAppointmentSelectionValues, "from">;
        return !from || startOfDay(till) >= startOfDay(from);
      }) as Yup.Schema<Date | null>,
    appointmentId: Yup.string().nullable().required(t("exchange.validation.appointmentRequired")) as Yup.Schema<
      string | null
    >,
  };
}

/** The Yup rules for what a requester wants in return, shared by publishing and editing a request. */
export function exchangeRequestedServicesRules(t: TFunction): {
  [K in keyof ExchangeRequestedServicesValues]: Yup.Schema<ExchangeRequestedServicesValues[K]>;
} {
  return {
    requestedPurposeKeys: Yup.array()
      .of(Yup.string().required())
      .min(1, t("exchange.validation.purposesRequired"))
      .required() as Yup.Schema<string[]>,
    // Formik hands an empty text field to Yup as undefined; ensure() turns it into "" so the note stays optional.
    note: Yup.string().ensure(),
  };
}
